// client/src/components/pages/DriverPWA/DriverDashboard.js
import React, { useState, useEffect } from 'react';
import {
  FaTachometerAlt,
  FaMapMarkerAlt,
  FaSatelliteDish,
  FaMountain,
  FaCubes,
  FaClock,
  FaSignOutAlt,
  FaSignInAlt,
  FaHourglassHalf,
} from 'react-icons/fa';

/**
 * Format a duration in milliseconds as HH:MM:SS
 */
function formatDuration(ms) {
  if (!ms || ms < 0) return '00:00:00';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds].map((v) => String(v).padStart(2, '0')).join(':');
}

/**
 * Format a Date as HH:MM
 */
function formatTime(date) {
  if (!date) return '--:--';
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export default function DriverDashboard({
  position,
  tracking,
  connected,
  currentZone,
  zoneEntryTime,
  shiftLog = [],
}) {
  const [dwellElapsed, setDwellElapsed] = useState(0);

  // Live dwell timer — counts up every second from zoneEntryTime
  useEffect(() => {
    if (!zoneEntryTime) {
      setDwellElapsed(0);
      return;
    }

    // Set initial value immediately
    setDwellElapsed(Date.now() - new Date(zoneEntryTime).getTime());

    const interval = setInterval(() => {
      setDwellElapsed(Date.now() - new Date(zoneEntryTime).getTime());
    }, 1000);

    return () => clearInterval(interval);
  }, [zoneEntryTime]);

  // Not tracking state
  if (!tracking) {
    return (
      <div className="p-4 text-center text-gray-400">
        <FaMapMarkerAlt className="mx-auto mb-2" size={32} />
        <p className="text-sm">Tap "Start Tracking" to begin sharing your location</p>
      </div>
    );
  }

  // Waiting for GPS
  if (!position) {
    return (
      <div className="p-4 text-center text-gray-400">
        <FaSatelliteDish className="mx-auto mb-2 animate-pulse" size={32} />
        <p className="text-sm">Acquiring GPS signal...</p>
      </div>
    );
  }

  // Metric cards data
  const metricCards = [
    {
      icon: <FaTachometerAlt size={18} />,
      label: 'Speed',
      value: position.speed !== null ? `${Math.round(position.speed)} km/h` : '--',
      color: 'text-blue-600',
      bg: 'bg-blue-50',
    },
    {
      icon: <FaMapMarkerAlt size={18} />,
      label: 'Accuracy',
      value: position.accuracy ? `${Math.round(position.accuracy)}m` : '--',
      color: 'text-amber-600',
      bg: 'bg-amber-50',
    },
    {
      icon: <FaMountain size={18} />,
      label: 'Altitude',
      value: position.altitude !== null ? `${Math.round(position.altitude)}m` : '--',
      color: 'text-purple-600',
      bg: 'bg-purple-50',
    },
  ];

  // Calculate total shift duration across all logged zones
  const totalShiftMinutes = shiftLog.reduce((sum, entry) => sum + (entry.durationMin || 0), 0);

  return (
    <div className="p-4 space-y-4">
      {/* Current Stockpile — prominent card */}
      <div
        className={`rounded-2xl p-5 border-2 ${
          currentZone ? 'bg-teal-50 border-teal-300' : 'bg-gray-50 border-gray-200'
        }`}
      >
        <div className="flex items-center gap-2 mb-2">
          <FaCubes className={currentZone ? 'text-teal-600' : 'text-gray-400'} size={20} />
          <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Current Stockpile
          </span>
        </div>
        <p className={`text-2xl font-bold ${currentZone ? 'text-teal-800' : 'text-gray-400'}`}>
          {currentZone || 'Not in any stockpile'}
        </p>
        {currentZone && zoneEntryTime && (
          <div className="mt-3 flex items-center gap-2">
            <FaClock className="text-teal-500" size={14} />
            <span className="text-lg font-mono font-semibold text-teal-700">
              {formatDuration(dwellElapsed)}
            </span>
            <span className="text-xs text-gray-400 ml-1">dwell time</span>
          </div>
        )}
      </div>

      {/* Metric cards row */}
      <div className="grid grid-cols-3 gap-3">
        {metricCards.map((card) => (
          <div key={card.label} className="bg-surface rounded-xl p-3 border border-gray-200">
            <div className={`mb-1 ${card.color}`}>{card.icon}</div>
            <p className="text-base font-bold text-gray-900">{card.value}</p>
            <p className="text-xs text-gray-400">{card.label}</p>
          </div>
        ))}
      </div>

      {/* Shift Summary */}
      {shiftLog.length > 0 && (
        <div className="bg-surface rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
            <FaHourglassHalf className="text-gray-500" size={14} />
            <span className="text-sm font-semibold text-gray-700">Shift Summary</span>
            <span className="ml-auto text-xs text-gray-400">
              {shiftLog.length} zone{shiftLog.length !== 1 ? 's' : ''}
            </span>
          </div>

          <div className="divide-y divide-gray-50">
            {/* Header row */}
            <div className="grid grid-cols-4 gap-1 px-4 py-2 text-xs font-medium text-gray-400 uppercase tracking-wider">
              <span>Zone</span>
              <span className="text-center">In</span>
              <span className="text-center">Out</span>
              <span className="text-right">Duration</span>
            </div>

            {/* Data rows */}
            {shiftLog.map((entry, idx) => (
              <div key={idx} className="grid grid-cols-4 gap-1 px-4 py-2.5 text-sm items-center">
                <span className="font-medium text-gray-800 truncate">{entry.zoneName}</span>
                <span className="text-center text-gray-500 flex items-center justify-center gap-1">
                  <FaSignInAlt size={10} className="text-green-400" />
                  {formatTime(entry.enteredAt)}
                </span>
                <span className="text-center text-gray-500 flex items-center justify-center gap-1">
                  <FaSignOutAlt size={10} className="text-red-400" />
                  {formatTime(entry.exitedAt)}
                </span>
                <span className="text-right font-mono text-gray-700">
                  {entry.durationMin.toFixed(1)}m
                </span>
              </div>
            ))}
          </div>

          {/* Total row */}
          <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500 uppercase">Total Zone Time</span>
            <span className="text-sm font-bold text-gray-800">
              {totalShiftMinutes < 60
                ? `${totalShiftMinutes.toFixed(1)} min`
                : `${Math.floor(totalShiftMinutes / 60)}h ${Math.round(totalShiftMinutes % 60)}m`}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
